/**
 * ============================================
 * SHARED BASE LIMITS
 * All sizes in bytes. Use these as building blocks.
 * ============================================
 */

import { env } from "../config/env"

// Image limits shared by all entities.
// Default 30MB; override via VITE_UPLOAD_IMAGE_MAX_BYTES (requires rebuild).
export const IMAGE_LIMITS = {
  MAX_FILE_SIZE: env.VITE_UPLOAD_IMAGE_MAX_BYTES,
  ALLOWED_TYPES: ["image/png", "image/jpeg", "image/jpg", "image/heic", "image/heif"] as const,
} as const

// Video limits shared by all entities.
// Default 100MB base; override via VITE_UPLOAD_VIDEO_MAX_BYTES (requires
// rebuild). Per-feature long-form overrides (ritual/profile/community) use
// LONG_FORM_MEDIA_LIMITS.MAX_FILE_SIZE instead -- see PROFILE_VIDEO_LIMITS /
// COMMUNITY_VIDEO_LIMITS / RITUAL_VIDEO_LIMITS.
export const VIDEO_LIMITS = {
  MAX_FILE_SIZE: env.VITE_UPLOAD_VIDEO_MAX_BYTES,
  ALLOWED_TYPES: ["video/mp4", "video/quicktime", "video/webm", "video/mpeg"] as const,
} as const

// Audio limits shared by all entities.
// Default 100MB base; override via VITE_UPLOAD_AUDIO_MAX_BYTES (requires
// rebuild). Ritual long-form override uses LONG_FORM_MEDIA_LIMITS below.
export const AUDIO_LIMITS = {
  MAX_FILE_SIZE: env.VITE_UPLOAD_AUDIO_MAX_BYTES,
  ALLOWED_TYPES: ["audio/mpeg", "audio/mp3", "audio/aac", "audio/wav", "audio/flac"] as const,
} as const

// Long-form media limit shared by profile/community/ritual video and ritual
// audio. Default 4GB; override via VITE_UPLOAD_LONG_FORM_MAX_BYTES (requires
// rebuild). Mirrors backend UPLOAD_VIDEO_MAX_BYTES / UPLOAD_AUDIO_MAX_BYTES.
export const LONG_FORM_MEDIA_LIMITS = {
  MAX_FILE_SIZE: env.VITE_UPLOAD_LONG_FORM_MAX_BYTES,
} as const

// Thumbnail limits (for video/audio poster images)
export const THUMBNAIL_LIMITS = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: ["image/png", "image/jpeg", "image/jpg", "image/heic", "image/heif"] as const,
} as const

/**
 * ============================================
 * PROFILE LIMITS
 * ============================================
 */

export const PROFILE_IMAGE_LIMITS = {
  ...IMAGE_LIMITS,
  MAX_GALLERY_PHOTOS: 3,
  MAX_COVER_PHOTOS: 1,
} as const

export const PROFILE_VIDEO_LIMITS = {
  MAX_FILE_SIZE: LONG_FORM_MEDIA_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: VIDEO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 1,
} as const

export const PROFILE_AUDIO_LIMITS = {
  MAX_FILE_SIZE: AUDIO_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: AUDIO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 1,
} as const

// Combined profile media limits
export const PROFILE_MEDIA_LIMITS = {
  IMAGES: PROFILE_IMAGE_LIMITS,
  VIDEOS: PROFILE_VIDEO_LIMITS,
  AUDIO: PROFILE_AUDIO_LIMITS,
  THUMBNAILS: THUMBNAIL_LIMITS,
} as const

/**
 * ============================================
 * COMMUNITY LIMITS
 * ============================================
 */

export const COMMUNITY_IMAGE_LIMITS = {
  ...IMAGE_LIMITS,
  MAX_FILES: 5,
} as const

export const COMMUNITY_VIDEO_LIMITS = {
  MAX_FILE_SIZE: LONG_FORM_MEDIA_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: VIDEO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 50,
} as const

export const COMMUNITY_AUDIO_LIMITS = {
  MAX_FILE_SIZE: AUDIO_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: AUDIO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 0, // Communities don't support standalone audio uploads
} as const

// Combined community media limits
export const COMMUNITY_MEDIA_LIMITS = {
  IMAGES: COMMUNITY_IMAGE_LIMITS,
  VIDEOS: COMMUNITY_VIDEO_LIMITS,
  AUDIO: COMMUNITY_AUDIO_LIMITS,
  THUMBNAILS: THUMBNAIL_LIMITS,
} as const

/**
 * ============================================
 * RITUAL LIMITS
 * ============================================
 */

export const RITUAL_IMAGE_LIMITS = {
  ...IMAGE_LIMITS,
  MAX_FILES: 5,
} as const

export const RITUAL_VIDEO_LIMITS = {
  MAX_FILE_SIZE: LONG_FORM_MEDIA_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: VIDEO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 5,
} as const

export const RITUAL_AUDIO_LIMITS = {
  MAX_FILE_SIZE: LONG_FORM_MEDIA_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: AUDIO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 5,
} as const

// Combined ritual media limits
export const RITUAL_MEDIA_LIMITS = {
  IMAGES: RITUAL_IMAGE_LIMITS,
  VIDEOS: RITUAL_VIDEO_LIMITS,
  AUDIO: RITUAL_AUDIO_LIMITS,
  THUMBNAILS: THUMBNAIL_LIMITS,
} as const

/**
 * ============================================
 * COACHING/SESSION LIMITS
 * ============================================
 */

export const SESSION_IMAGE_LIMITS = {
  ...IMAGE_LIMITS,
  MAX_FILES: 3,
} as const

export const SESSION_VIDEO_LIMITS = {
  MAX_FILE_SIZE: VIDEO_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: VIDEO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 3,
} as const

export const SESSION_AUDIO_LIMITS = {
  MAX_FILE_SIZE: AUDIO_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: AUDIO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 3,
} as const

// Combined session/coaching media limits
export const SESSION_MEDIA_LIMITS = {
  IMAGES: SESSION_IMAGE_LIMITS,
  VIDEOS: SESSION_VIDEO_LIMITS,
  AUDIO: SESSION_AUDIO_LIMITS,
  THUMBNAILS: THUMBNAIL_LIMITS,
} as const

/**
 * ============================================
 * COURSE MODULE LIMITS (within Rituals)
 * ============================================
 */

export const COURSE_MODULE_VIDEO_LIMITS = {
  MAX_FILE_SIZE: VIDEO_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: VIDEO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 1, // One video per module
} as const

export const COURSE_MODULE_AUDIO_LIMITS = {
  MAX_FILE_SIZE: AUDIO_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: AUDIO_LIMITS.ALLOWED_TYPES,
  MAX_FILES: 1, // One audio per module
} as const

// Combined course module media limits
export const COURSE_MODULE_MEDIA_LIMITS = {
  VIDEOS: COURSE_MODULE_VIDEO_LIMITS,
  AUDIO: COURSE_MODULE_AUDIO_LIMITS,
  IMAGES: IMAGE_LIMITS, // For thumbnails/poster images
  THUMBNAILS: THUMBNAIL_LIMITS,
} as const

/**
 * ============================================
 * LEGACY EXPORTS (backward compatibility)
 * ============================================
 */

// Keep old names as aliases for backward compatibility during migration
export const MEDIA_UPLOAD_LIMITS = {
  MAX_IMAGES: 10,
  MAX_VIDEOS: 100,
} as const

// Legacy aliases
export const PROFILE_PHOTO_LIMITS = PROFILE_IMAGE_LIMITS
export const COACHING_IMAGE_LIMITS = SESSION_IMAGE_LIMITS
export const COACHING_VIDEO_LIMITS = SESSION_VIDEO_LIMITS

/**
 * ============================================
 * MODAL DEFAULTS
 * Default values for MediaUploadModal component
 * ============================================
 */

export const MODAL_DEFAULTS = {
  MAX_FILES: 10,
  MAX_IMAGE_FILE_SIZE: IMAGE_LIMITS.MAX_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE: VIDEO_LIMITS.MAX_FILE_SIZE,
  MAX_AUDIO_FILE_SIZE: AUDIO_LIMITS.MAX_FILE_SIZE,
  ALLOWED_TYPES: [...IMAGE_LIMITS.ALLOWED_TYPES],
} as const
